//  ----------      START   IMPORT  BLOCK       ----------
import { Row, Col, Form } from "react-bootstrap";
import { FaAppleAlt, FaHeartbeat, FaVenusMars } from "react-icons/fa";
// Context
import { useLanguage } from "../../../../../API/Context/LanguageContext";
// Config & Maps
import { VocabProfileOnboarding as Vocab } from "../../../../../ConfigFiles/multilingual/VocabProfileOnboarding";
import { FITNESS_FREQUENCY_MAP } from "../../../../../ConfigFiles/maps/habit_frequency_map";
import { FITNESS_MAP } from "../../../../../ConfigFiles/maps/fitness_map";
import { DIET_CATEGORIES_MAP, LITERS_PER_DAY_MAP } from "../../../../../ConfigFiles/maps/diet_map";
import { SEX_BARRIER_MAP, PARTNERS_RANGE_MAP } from "../../../../../ConfigFiles/maps/sex_map";
import { DietCategoriesEnum } from "../../../../../Types/Enums/DietCategoriesEnum";
//  ----------      END   IMPORT  BLOCK       ----------

const StepGeneralHealth = ({ data, update }) => {
  // Consume context
  const { lan } = useLanguage();
    
  const handleNested = (category, field, val, isNumeric = false) => {
    let processedValue = val;
    if (isNumeric) {
      processedValue = val === "" ? "" : parseInt(val, 10) || 0;
    }
    
    update({ 
      [category]: { 
        ...data[category], 
        [field]: processedValue 
      } 
    });
  };

  return (
    <div className="animate-in">
      <h4 className="mb-4 border-start border-primary border-4 ps-3">
        {Vocab.general_health_section_title[lan]}
      </h4>

      {/* Fitness Section */}
      <div className="habit-group mb-4 p-3 rounded-3 border shadow-sm bg-white">
        <h6 className="fw-bold"><FaHeartbeat className="me-2 text-danger"/>{Vocab.physical_activity_title[lan]}</h6>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label className="small fw-bold">Nivel / Level</Form.Label>
            <Form.Select 
              value={data.fitness.level} 
              onChange={e => handleNested('fitness', 'level', e.target.value)}
            >
              <option value="" disabled hidden>{lan === 0 ? "Seleccionar..." : "Select..."}</option>
              {FITNESS_MAP.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.labels[lan]}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6}>
            <Form.Label className="small fw-bold">Frecuencia / Frequency</Form.Label>
            <Form.Select 
              value={data.fitness.frequency_per_week}
              onChange={e => handleNested('fitness', 'frequency_per_week', e.target.value)}
            >
              <option value="" disabled hidden>{lan === 0 ? "Seleccionar..." : "Select..."}</option>
              {FITNESS_FREQUENCY_MAP.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.labels[lan]}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </div>

      {/* Diet Habits Section */}
      <div className="habit-group mb-4 p-3 rounded-3 border shadow-sm bg-white">
        <h6 className="fw-bold mb-3"><FaAppleAlt className="me-2 text-success"/>{Vocab.diet_habits_title[lan]}</h6>
        <Row className="g-3">
          <Col md={3}>
            <Form.Label className="small fw-bold">{Vocab.meals_day[lan]}</Form.Label>
            <Form.Control 
              type="number" 
              value={data.diet.meals_per_day} 
              onChange={e => handleNested('diet', 'meals_per_day', e.target.value, true)} 
            />
          </Col>
          <Col md={3}>
            <Form.Label className="small fw-bold">{Vocab.water_intake[lan]} (L)</Form.Label>
            <Form.Select 
              value={data.diet.water_liters} 
              onChange={e => handleNested('diet', 'water_liters', e.target.value)}
            >
              <option value="" disabled hidden>{lan === 0 ? "Seleccionar..." : "Select..."}</option>
              {LITERS_PER_DAY_MAP.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.labels[lan]}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Form.Label className="small fw-bold">{Vocab.junk_food_freq[lan]}</Form.Label>
            <Form.Select 
              value={data.diet.frequency_junk_food} 
              onChange={e => handleNested('diet', 'frequency_junk_food', e.target.value)}
            >
              <option value="" disabled hidden>{lan === 0 ? "Seleccionar..." : "Select..."}</option>
              {FITNESS_FREQUENCY_MAP.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.labels[lan]}</option>
              ))}
            </Form.Select>
          </Col>

          {/* Special Diet Direct Selection */}
          <Col md={3}>
            <Form.Label className="small fw-bold">{Vocab.special_diet[lan]}</Form.Label>
            <Form.Select 
              value={data.diet.diet_type || DietCategoriesEnum.NONE} 
              onChange={e => handleNested('diet', 'diet_type', e.target.value)}
            >
              {DIET_CATEGORIES_MAP.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.labels[lan]}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      </div>

      {/* Sexual Health Section */}
      <div className="habit-group mb-4 p-3 rounded-3 border shadow-sm bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold mb-0">
            <FaVenusMars className="me-2 text-primary"/>{Vocab.sexual_activity_title[lan]}
          </h6>
          <Form.Check 
            type="switch" 
            id="sexual-active-switch"
            checked={data.sexual_health.status}
            onChange={(e) => handleNested('sexual_health', 'status', e.target.checked)} 
          />
        </div>

        {data.sexual_health.status && (
          <Row className="g-3 animate-in">
            <Col md={6}>
              <Form.Label className="small fw-bold">{Vocab.partners_range[lan]}</Form.Label>
              <Form.Select 
                value={data.sexual_health.partners_range}
                onChange={(e) => handleNested('sexual_health', 'partners_range', e.target.value)}
              >
                <option value="" disabled hidden>{lan === 0 ? "Seleccionar..." : "Select..."}</option>
                {PARTNERS_RANGE_MAP.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.labels[lan]}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={6}>
              <Form.Label className="small fw-bold">{Vocab.barrier_method[lan]}</Form.Label>
              <Form.Select 
                value={data.sexual_health.protection}
                onChange={(e) => handleNested('sexual_health', 'protection', e.target.value)}
              >
                <option value="" disabled hidden>{lan === 0 ? "Seleccionar..." : "Select..."}</option>
                {SEX_BARRIER_MAP.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.labels[lan]}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default StepGeneralHealth;